import { useState, useRef, useEffect, FormEvent } from "react";

export default function ContactSection() {
  const [visible, setVisible] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const data = new URLSearchParams(new FormData(e.currentTarget) as unknown as URLSearchParams);
    try {
      await fetch("https://readdy.ai/api/form/d76hfqtr5g02gnd6a0tg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 lg:py-28 bg-[#F5F0EB]" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-['DM_Sans'] text-xs font-medium tracking-[0.3em] uppercase text-[#C9A87C] mb-3">Contact</p>
          <h2 className="font-['Cormorant_Garamond'] text-5xl lg:text-6xl font-light text-[#1A1A1A]">お問い合わせ</h2>
          <p className="font-['Noto_Sans_JP'] text-sm text-[#6B6B6B] mt-3">ご不明な点はお気軽にお問い合わせください</p>
        </div>

        <div className={`flex flex-col lg:flex-row gap-10 transition-all duration-700 delay-100 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          {/* Info */}
          <div className="w-full lg:w-[38%] space-y-5">
            <div className="bg-white rounded-[20px] p-6">
              <h3 className="font-['Cormorant_Garamond'] text-xl font-medium text-[#1A1A1A] mb-5">サロン情報</h3>
              <div className="space-y-4">
                {[
                  { icon: "ri-map-pin-line", label: "住所", value: "東京都渋谷区神宮前3-5-12\nNANAビル2F" },
                  { icon: "ri-phone-line", label: "電話", value: "03-1234-5678" },
                  { icon: "ri-mail-line", label: "メール", value: "info@ateliernana.jp" },
                  { icon: "ri-time-line", label: "営業時間", value: "月〜土 10:00〜20:00\n日・祝 10:00〜18:00（水曜定休）" },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#F5F0EB] rounded-full flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-[#C9A87C] text-sm`}></i>
                    </div>
                    <div>
                      <p className="font-['DM_Sans'] text-[10px] tracking-widest uppercase text-[#9E9189] mb-0.5">{item.label}</p>
                      <p className="font-['Noto_Sans_JP'] text-sm text-[#3A3A3A] whitespace-pre-line">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Map */}
            <div className="rounded-[20px] overflow-hidden h-[200px]">
              <iframe
                title="Atelier NANA Map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3241.076!2d139.7085!3d35.6657!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x60188ca2e5f1f4b7%3A0x0!2z5p2x5Lqs6YO96riL6LC35Yy65YaF5a6554K555S6!5e0!3m2!1sja!2sjp!4v1680000000000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
            <div className="flex gap-3">
              {[
                { icon: "ri-instagram-line", label: "Instagram", href: "https://instagram.com" },
                { icon: "ri-line-fill", label: "LINE", href: "https://line.me" },
                { icon: "ri-twitter-x-line", label: "Twitter", href: "https://twitter.com" },
              ].map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="nofollow noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 bg-white rounded-[12px] py-3 text-sm font-['DM_Sans'] text-[#3D2E2A] hover:bg-[#3D2E2A] hover:text-white transition-colors duration-200 cursor-pointer">
                  <i className={`${s.icon} text-base`}></i>
                  <span className="text-xs">{s.label}</span>
                </a>
              ))}
            </div>
          </div>

          {/* Form */}
          <div className="w-full lg:flex-1">
            <div className="bg-white rounded-[24px] p-8">
              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 flex items-center justify-center bg-[#F5F0EB] rounded-full mx-auto mb-5">
                    <i className="ri-check-line text-3xl text-[#C9A87C]"></i>
                  </div>
                  <h3 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#1A1A1A] mb-3">お問い合わせを受け付けました</h3>
                  <p className="font-['Noto_Sans_JP'] text-sm text-[#6B6B6B]">2営業日以内にご返信いたします。</p>
                  <button onClick={() => setSubmitted(false)} className="mt-5 text-sm text-[#C9A87C] underline cursor-pointer font-['Noto_Sans_JP']">
                    別のお問い合わせをする
                  </button>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                  <h3 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#1A1A1A] mb-6">お問い合わせフォーム</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-['Noto_Sans_JP'] text-xs text-[#6B6B6B] mb-2">お名前 <span className="text-[#C9A87C]">*</span></label>
                      <input name="name" required type="text" placeholder="山田 花子" className="w-full border border-[#E8E4DF] rounded-lg px-4 py-3 text-sm font-['Noto_Sans_JP'] outline-none focus:border-[#C9A87C] transition-colors bg-[#FAFAF8]" />
                    </div>
                    <div>
                      <label className="block font-['Noto_Sans_JP'] text-xs text-[#6B6B6B] mb-2">メールアドレス <span className="text-[#C9A87C]">*</span></label>
                      <input name="email" required type="email" placeholder="example@email.com" className="w-full border border-[#E8E4DF] rounded-lg px-4 py-3 text-sm font-['Noto_Sans_JP'] outline-none focus:border-[#C9A87C] transition-colors bg-[#FAFAF8]" />
                    </div>
                  </div>
                  <div>
                    <label className="block font-['Noto_Sans_JP'] text-xs text-[#6B6B6B] mb-2">お問い合わせ種別</label>
                    <select name="type" className="w-full border border-[#E8E4DF] rounded-lg px-4 py-3 text-sm font-['Noto_Sans_JP'] outline-none focus:border-[#C9A87C] transition-colors bg-[#FAFAF8] cursor-pointer">
                      <option>メニューについて</option>
                      <option>予約について</option>
                      <option>料金について</option>
                      <option>アクセスについて</option>
                      <option>その他</option>
                    </select>
                  </div>
                  <div>
                    <label className="block font-['Noto_Sans_JP'] text-xs text-[#6B6B6B] mb-2">お問い合わせ内容 <span className="text-[#C9A87C]">*</span></label>
                    <textarea name="message" required rows={5} maxLength={500} placeholder="お問い合わせ内容をご記入ください（500字以内）" className="w-full border border-[#E8E4DF] rounded-lg px-4 py-3 text-sm font-['Noto_Sans_JP'] outline-none focus:border-[#C9A87C] transition-colors bg-[#FAFAF8] resize-none" />
                  </div>
                  <button type="submit" disabled={loading} className="w-full bg-[#3D2E2A] text-white text-sm font-medium py-4 rounded-full hover:bg-[#C9A87C] transition-colors duration-300 cursor-pointer whitespace-nowrap font-['DM_Sans'] tracking-wider disabled:opacity-60">
                    {loading ? "送信中..." : "送信する"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
